// System Health page as specified in appmark.txt
import React, { useEffect, useState } from "react";
import { HeartIcon } from "lucide-react";
import api from "../services/api";

interface LLMProvider {
  id: string;
  name: string;
  type: string;
  model: string;
  enabled: boolean;
  default: boolean;
}

export const Health: React.FC = () => {
  const [providers, setProviders] = useState<LLMProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        setLoading(true);
        const data = await api.getLLMProviders();
        setProviders(data);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch LLM providers:", err);
        setError("Failed to load LLM providers");
      } finally {
        setLoading(false);
      }
    };

    fetchProviders();
  }, []);

  return (
    <div className="space-y-6" data-testid="health-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          System Health
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LLM Providers */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            LLM Providers
          </h2>
          {loading ? (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Loading providers...
            </div>
          ) : error ? (
            <div className="text-sm text-red-500">{error}</div>
          ) : providers.length === 0 ? (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              No providers configured
            </div>
          ) : (
            <div className="space-y-3">
              {providers.map((provider) => (
                <div key={provider.id} className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {provider.name}
                      {provider.default && (
                        <span className="ml-2 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 rounded">
                          Default
                        </span>
                      )}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {provider.type} • {provider.model}
                    </span>
                  </div>
                  <span className="px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 rounded">
                    Enabled
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* External Services */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            External Services
          </h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Jira
              </span>
              <span className="px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 rounded">
                Online
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                GitHub
              </span>
              <span className="px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 rounded">
                Online
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Database
              </span>
              <span className="px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 rounded">
                Online
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="text-center py-12">
          <HeartIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            System Health Monitoring
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            Detailed health monitoring features will be implemented here.
          </p>
        </div>
      </div>
    </div>
  );
};
